import { t } from "i18next";
import { Chip, Typography } from "@material-tailwind/react";

interface OdooFieldMappingFormProps {
  data?: EasyTech.Odoo.FieldMapping;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
}

export default function OdooFieldMappingForm({
  data,
  onChange,
  submitted
}: OdooFieldMappingFormProps) {
  const isInvalid = submitted && (!data?.odooField || data.odooField.trim().length === 0);

  const getTypeLabel = (type?: EasyTech.Odoo.FieldType): string => {
    switch (type) {
      case "BOOLEAN":
        return t("admin:odoo.types.BOOLEAN", "Booléen");
      case "STRING":
        return t("admin:odoo.types.STRING", "Texte");
      case "INTEGER":
        return t("admin:odoo.types.INTEGER", "Entier");
      case "DECIMAL":
        return t("admin:odoo.types.DECIMAL", "Décimal");
      case "DATE":
        return t("admin:odoo.types.DATE", "Date");
      default:
        return type || "";
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Informations en lecture seule */}
      <div className="bg-gray-50 border border-gray-200 rounded p-3 flex flex-col gap-2">
        <div>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {t("admin:odoo.form.parameterLabel", "Paramètre EasyTech")}
          </span>
          <Typography variant="small" className="font-medium text-gray-800">
            {data?.label}
          </Typography>
          <code className="text-xs text-gray-500 font-mono">{data?.fieldKey}</code>
        </div>

        <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {t("admin:odoo.form.typeLabel", "Type :")}
          </span>
          <Chip
            size="sm"
            variant="ghost"
            color={data?.fieldType === "BOOLEAN" ? "green" : "blue"}
            value={getTypeLabel(data?.fieldType)}
            className="capitalize py-0.5 px-2 text-xs"
          />
        </div>
      </div>

      {/* Champ modifiable : Nom technique dans Odoo */}
      <div className="field">
        <label htmlFor="odooField" className="font-medium text-gray-700">
          {t("admin:odoo.form.odooFieldLabel", "Nom technique du champ dans Odoo")} *
        </label>
        <input
          id="odooField"
          type="text"
          value={data?.odooField || ""}
          onChange={(e) => onChange("odooField", e.target.value)}
          className={`font-mono ${isInvalid ? "error border-red-500" : ""}`}
          placeholder={t("admin:odoo.form.odooFieldPlaceholder", "ex : x_studio_purchase_order_mandatory_1")}
        />
        {isInvalid && (
          <span className="text-xs text-red-500">
            {t("common:mandatoryField", "Ce champ est obligatoire")}
          </span>
        )}
      </div>

      {/* Champ modifiable : Description */}
      <div className="field">
        <label htmlFor="description" className="font-medium text-gray-700">
          {t("admin:odoo.form.descriptionLabel", "Description métier")}
        </label>
        <textarea
          id="description"
          rows={3}
          value={data?.description || ""}
          onChange={(e) => onChange("description", e.target.value)}
          className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
          placeholder={t("admin:odoo.form.descriptionPlaceholder", "Description du rôle de ce champ...")}
        />
      </div>
    </div>
  );
}
